import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    CircularProgress,
    Avatar,
    Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useUserAnalyticsDetails } from "../features/users/useUsers";
import { useState } from "react";

export default function UserAnalyticsDetails({ open, onClose, filters }) {
    const theme = useTheme();
    // eslint-disable-next-line no-unused-vars
    const [page, setPage] = useState(0);
    const rowsPerPage = 10000;

    const { data, isLoading } = useUserAnalyticsDetails({ ...filters, page, limit: rowsPerPage }, { enabled: open });

    const users = data?.items || [];

    const getTitle = () => {
        if (!filters.type) return "Details";
        let typeLabel = filters.type;
        // make it readable
        if(typeLabel === "authProvider") typeLabel = "Auth Provider";
        else if(typeLabel === "hasPhone") typeLabel = "Phone Number";
        else if(typeLabel === "hasReferral") typeLabel = "Referral";
        else if(typeLabel === "hasRegisterIp") typeLabel = "Register IP";
        else if(typeLabel === "month_label") typeLabel = "Month";
        
        typeLabel = typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1);
        
        let valueLabel = filters.value;
        if (filters.type === 'month_label' && !isNaN(Number(filters.value))) {
             valueLabel = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(filters.value)];
        }

        return `${typeLabel} Details: ${valueLabel || ''} (${filters.year === 'all' ? 'All Years' : filters.year || ''})`;
    };
    
    // Helper for Status Chip
    const getStatusChip = (isVerified) => {
        return (
            <Chip 
                label={isVerified ? "Verified" : "Unverified"} 
                size="small" 
                color={isVerified ? "success" : "error"} 
                variant="outlined"
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: "60vh",
                    bgcolor: theme.palette.background.default
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="div">
                    {getTitle()}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : users.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography>No records found for this selection.</Typography>
                    </Box>
                ) : (
                    <TableContainer component={Box} elevation={0} variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Auth Provider</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Location</TableCell>
                                    <TableCell>Created At</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((user) => (
                                    <TableRow key={user._id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    src={user.profilePicture}
                                                    alt={user.firstName}
                                                    sx={{ width: 32, height: 32 }}
                                                >
                                                    {user.email?.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {user.firstName} {user.lastName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {user.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{user.role}</TableCell>
                                        <TableCell>{getStatusChip(user.isVerified)}</TableCell>
                                        <TableCell>
                                            {user.authProvider && user.authProvider.length > 0 ? (
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {user.authProvider.map(p => (
                                                        <Chip key={p} label={p} size="small" variant="outlined" />
                                                    ))}
                                                </Box>
                                            ) : 'Password'}
                                        </TableCell>
                                        <TableCell>{user.phoneNumber || '-'}</TableCell>
                                        <TableCell>
                                            {user.ipInfo ? `${user.ipInfo.city || ''}, ${user.ipInfo.country || ''}` : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(user.createdAt).toLocaleDateString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
}
